import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { safeClipboardWrite } from '../utils/safeStorage';
import { Submission, MediaType, Participant, Project } from '../types';
import { XIcon, MicIcon, TypeIcon, DownloadIcon, ShareIcon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon, ClockIcon, UserIcon, MaximizeIcon, MinimizeIcon, ZoomInIcon, ZoomOutIcon, RefreshCwIcon, ChevronDownIcon, CakeIcon, UsersIcon, BriefcaseIcon, CheckSquareIcon, ClipboardCopyIcon, GridIcon, EditIcon, PlayIcon, MonitorIcon, CheckIcon, MailIcon } from './icons';
import { generateTagsForSubmission, generateAudioAnalysis } from '../services/geminiService';
import ImageWithLoader from './ImageWithLoader';
import { WEBM_OPUS_PLAYABLE, isWebmUrl, WEBM_AUDIO_FALLBACK_TEXT } from './shared/media/MediaDisplay';
import { AnnotatedPhotoView } from './AnnotatedPhotoView';
import { AnnotationTranscriptRail } from './AnnotationTranscriptRail';
import Avatar from './Avatar';
import { useAuthContext } from '../contexts/AuthContext';
import { ConversationThread } from './thread/ConversationThread';
import { requestRedo } from '../services/firestoreService';
import { useAppData } from '../contexts/AppDataContext';
import { ref, getBlob } from 'firebase/storage';
import { storage } from '../services/firebaseConfig';
import JSZip from 'jszip';
import { fetchBlobWithProxy } from '../utils/mediaUtils';

const Lightbox: React.FC<{ mediaType: MediaType, mediaUrl: string, onClose: () => void }> = ({ mediaType, mediaUrl, onClose }) => {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const imageRef = useRef<HTMLImageElement>(null);
    const startPanRef = useRef({ x: 0, y: 0 });

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const newZoom = Math.max(1, Math.min(5, zoom - e.deltaY * 0.01));
        setZoom(newZoom);
        if (newZoom === 1) {
            setPan({ x: 0, y: 0 }); // Reset pan when fully zoomed out
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (zoom > 1) {
            e.preventDefault();
            setIsPanning(true);
            startPanRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
            if (imageRef.current) imageRef.current.style.cursor = 'grabbing';
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isPanning) {
            e.preventDefault();
            setPan({ x: e.clientX - startPanRef.current.x, y: e.clientY - startPanRef.current.y });
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsPanning(false);
        if (imageRef.current) imageRef.current.style.cursor = 'grab';
    };

    const handleReset = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[100] flex items-center justify-center animate-fade-in-down" onClick={onClose}>
            <button
                onClick={onClose}
                className="absolute top-6 right-6 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all z-[110]"
            >
                <XIcon className="w-8 h-8" />
            </button>

            {mediaType === MediaType.Photo && (
                <>
                    <div
                        className="relative w-full h-full flex items-center justify-center overflow-hidden p-4"
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                    >
                        <img
                            ref={imageRef}
                            src={mediaUrl}
                            alt="Lightbox view"
                            className="max-w-full max-h-full object-contain transition-transform duration-75 select-none"
                            style={{
                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                cursor: zoom > 1 ? 'grab' : 'default',
                            }}
                            onWheel={handleWheel}
                            onMouseDown={handleMouseDown}
                            draggable={false}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 p-3 bg-neutral-900/80 rounded-2xl backdrop-blur-md border border-white/10 z-[110]">
                        <button onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(1, z - 0.5)); }} className="p-2 text-white/80 hover:text-white transition-colors"><ZoomOutIcon /></button>
                        <div className="w-px h-6 bg-white/20"></div>
                        <button onClick={(e) => { e.stopPropagation(); handleReset(); }} className="p-2 text-white/80 hover:text-status-warn transition-colors"><RefreshCwIcon /></button>
                        <div className="w-px h-6 bg-white/20"></div>
                        <button onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(5, z + 0.5)); }} className="p-2 text-white/80 hover:text-white transition-colors"><ZoomInIcon /></button>
                    </div>
                </>
            )}

            {mediaType === MediaType.Video || mediaType === MediaType.Screen ? (
                <div className="max-w-5xl max-h-full p-4 w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
                    <video src={mediaUrl} controls autoPlay className="max-w-full max-h-full object-contain shadow-2xl rounded-lg outline-none">Your browser does not support the video tag.</video>
                </div>
            ) : null}
        </div>,
        document.body
    );
};


interface SubmissionModalProps {
    data: {
        submission: Submission;
        participant?: Participant;
    };
    onClose: () => void;
    currentProject: Project | null;
    onUpdateTags: (submissionId: string, newTags: string[]) => void;
    onUpdateNotes: (submissionId: string, notes: string) => void;
    onUpdateTranscript?: (submissionId: string, transcript: string) => void;
    onShare: () => void;
    taskName?: string;
    seekToTime?: string;
    highlightQuote?: string;
    onToggleReview?: (submissionId: string) => void;
}

type TagGenerationState = 'idle' | 'loading' | 'loaded' | 'error';

const formatTimestampForFilename = (isoString: string): string => {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};

const sanitizeFilename = (name: string) => {
    return name.replace(/[^a-zA-Z0-9_.-]/g, '_').replace(/\s+/g, '_');
};

const SubmissionModal: React.FC<SubmissionModalProps> = ({ data, onClose, currentProject, onUpdateTags, onUpdateNotes, onUpdateTranscript, onShare, taskName, seekToTime, highlightQuote, onToggleReview }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { submission, participant } = data;
    const { user: authUser, userProfile, claimsOrgId } = useAuthContext();
    const { appData } = useAppData();
    const [tagState, setTagState] = useState<TagGenerationState>('idle');
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    // canPlayType can answer "maybe" and still fail to decode (Safari) — a
    // real playback error also flips the WebM audio fallback. Reset per item.
    const [audioBroken, setAudioBroken] = useState(false);
    useEffect(() => { setAudioBroken(false); }, [currentMediaIndex]);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [showParticipantDetails, setShowParticipantDetails] = useState(false);
    const [selectedPinId, setSelectedPinId] = useState<string | null>(null);

    useEffect(() => { setSelectedPinId(null); }, [currentMediaIndex]);

    // Redo request state
    const [showRedoConfirm, setShowRedoConfirm] = useState(false);
    const [redoReason, setRedoReason] = useState('');
    const [isRequestingRedo, setIsRequestingRedo] = useState(false);
    const [redoRequested, setRedoRequested] = useState(!!submission.redoRequestedAt);

    // Task + redo eligibility
    const task = appData?.tasks.find(t => t.id === submission.taskId);
    const attemptCount = appData?.submissions.filter(
        s => s.participantId === submission.participantId && s.taskId === submission.taskId
    ).length || 1;
    const canRequestRedo = task?.allowRedo
        && !redoRequested
        && (!task.maxAttempts || attemptCount < task.maxAttempts);

    const videoRef = React.useRef<HTMLVideoElement>(null);
    const faceVideoRef = React.useRef<HTMLVideoElement>(null);
    const videoContainerRef = React.useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = () => {
        if (!videoContainerRef.current) return;
        if (!document.fullscreenElement) {
            videoContainerRef.current.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);
    const audioRef = React.useRef<HTMLAudioElement>(null);
    const [showPip, setShowPip] = useState(true);

    React.useEffect(() => {
        if (seekToTime) {
            const parseTimeToSeconds = (timeStr: string) => {
                const parts = timeStr.trim().split(':').map(Number);
                if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
                if (parts.length === 2) return parts[0] * 60 + parts[1];
                return 0;
            };
            const seconds = parseTimeToSeconds(seekToTime);
            if ((submission.mediaType === MediaType.Video || submission.mediaType === MediaType.Screen) && videoRef.current) {
                videoRef.current.currentTime = seconds;
                videoRef.current.play().catch(e => console.error("Auto-play prevented", e));
            } else if (submission.mediaType === MediaType.Audio && audioRef.current) {
                audioRef.current.currentTime = seconds;
                audioRef.current.play().catch(e => console.error("Auto-play prevented", e));
            }
        }
    }, [seekToTime, submission.mediaType, currentMediaIndex]);

    // PiP Synchronization Logic
    useEffect(() => {
        const video = videoRef.current;
        const faceVideo = faceVideoRef.current;
        if (!video || !faceVideo || !showPip) return;

        const syncFace = () => {
            if (Math.abs(faceVideo.currentTime - video.currentTime) > 0.3) {
                faceVideo.currentTime = video.currentTime;
            }
        };

        const handlePlay = () => {
            faceVideo.play().catch(e => console.warn("Face video play blocked:", e));
        };

        const handlePause = () => faceVideo.pause();

        const handleSeeking = () => {
            faceVideo.currentTime = video.currentTime;
        };

        const handleRateChange = () => {
            faceVideo.playbackRate = video.playbackRate;
        };

        // Workaround for WebM black screen on load/seek
        const wakeVideo = () => {
            faceVideo.currentTime = video.currentTime + 0.1;
            setTimeout(() => { faceVideo.currentTime = video.currentTime; }, 50);
        };

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('seeking', handleSeeking);
        video.addEventListener('ratechange', handleRateChange);
        video.addEventListener('timeupdate', syncFace);

        // Initial sync and wake
        faceVideo.onloadedmetadata = wakeVideo;
        if (faceVideo.readyState >= 1) wakeVideo();

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('seeking', handleSeeking);
            video.removeEventListener('ratechange', handleRateChange);
            video.removeEventListener('timeupdate', syncFace);
        };
    }, [currentMediaIndex, submission.id, showPip]);

    const renderHighlightedText = (text: string, highlight?: string) => {
        if (!highlight) return text;
        const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape regex chars
        const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === highlight.toLowerCase() ?
                <mark key={i} className="bg-status-warn-bg text-status-warn rounded px-1 font-bold animate-pulse">{part}</mark> :
                part
        );
    };
    const [currentTags, setCurrentTags] = useState<string[]>(submission.tags);
    const [currentNotes, setCurrentNotes] = useState<string>(submission.notes || '');
    const [currentTranscript, setCurrentTranscript] = useState<string>(submission.transcript || '');
    const [newTag, setNewTag] = useState('');
    const [isAnalyzingAudio, setIsAnalyzingAudio] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchTags = async () => {
            // Only generate tags if: no tags exist, notes exist, and we haven't already started
            if (submission && submission.tags.length === 0 && submission.notes && tagState === 'idle') {
                setTagState('loading');
                const generatedTags = await generateTagsForSubmission(submission.notes);
                if (cancelled) return;
                if (generatedTags) {
                    onUpdateTags(submission.id, generatedTags);
                    setCurrentTags(generatedTags);
                    setTagState('loaded');
                } else {
                    setTagState('error');
                }
            }
        };
        fetchTags();
        return () => { cancelled = true; };
    }, [submission.id]); // Only re-run when viewing a different submission

    useEffect(() => {
        setCurrentTags(submission.tags);
    }, [submission.tags]);

    useEffect(() => {
        setCurrentNotes(submission.notes || '');
    }, [submission.notes]);

    /* Transcription finishes in a Cloud Function well after the modal opens, so
       the transcript arrives as a prop change like tags and notes do. Without
       this the pane stayed empty until a full page reload. */
    useEffect(() => {
        setCurrentTranscript(submission.transcript || '');
    }, [submission.transcript]);

    // Reset media index when switching submissions
    useEffect(() => {
        setCurrentMediaIndex(0);
    }, [submission.id]);


    if (!submission) return null;

    const { mediaType, timestamp, notes, location } = submission;
    const participantName = (participant && participant.name) || 'Unknown Participant';

    const handleExportJson = () => {
        const exportData = {
            submissionId: submission.id,
            projectId: submission.projectId,
            participant: {
                id: participant && participant.id,
                name: participantName,
            },
            mediaType: submission.mediaType,
            mediaUrls: submission.mediaUrls,
            timestamp: submission.timestamp,
            location: submission.location,
            notes: submission.notes,
            tags: currentTags,
        };

        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dekko-submission-${submission.id}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Helper function to convert WebM audio to WAV format
    const convertWebMToWav = async (blob: Blob): Promise<Blob> => {
        try {
            // Create an audio context
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

            // Convert blob to array buffer
            const arrayBuffer = await blob.arrayBuffer();

            // Decode the audio data
            const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

            // Convert AudioBuffer to WAV
            const wavBlob = audioBufferToWav(audioBuffer);

            // Close the audio context to free resources
            audioContext.close();

            return wavBlob;
        } catch (error) {
            console.error("Audio conversion failed:", error);
            // Return original blob if conversion fails
            return blob;
        }
    };

    // Helper function to convert AudioBuffer to WAV blob
    const audioBufferToWav = (audioBuffer: AudioBuffer): Blob => {
        const numberOfChannels = audioBuffer.numberOfChannels;
        const sampleRate = audioBuffer.sampleRate;
        const format = 1; // PCM
        const bitDepth = 16;

        const bytesPerSample = bitDepth / 8;
        const blockAlign = numberOfChannels * bytesPerSample;

        const data = new Float32Array(audioBuffer.length * numberOfChannels);

        // Interleave channels
        for (let i = 0; i < audioBuffer.length; i++) {
            for (let channel = 0; channel < numberOfChannels; channel++) {
                const channelData = audioBuffer.getChannelData(channel);
                data[i * numberOfChannels + channel] = channelData[i];
            }
        }

        // Convert float samples to 16-bit PCM
        const samples = new Int16Array(data.length);
        for (let i = 0; i < data.length; i++) {
            const s = Math.max(-1, Math.min(1, data[i]));
            samples[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        const dataLength = samples.length * bytesPerSample;
        const buffer = new ArrayBuffer(44 + dataLength);
        const view = new DataView(buffer);

        // Write WAV header
        const writeString = (offset: number, string: string) => {
            for (let i = 0; i < string.length; i++) {
                view.setUint8(offset + i, string.charCodeAt(i));
            }
        };

        writeString(0, 'RIFF');
        view.setUint32(4, 36 + dataLength, true);
        writeString(8, 'WAVE');
        writeString(12, 'fmt ');
        view.setUint32(16, 16, true); // fmt chunk size
        view.setUint16(20, format, true);
        view.setUint16(22, numberOfChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * blockAlign, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, bitDepth, true);
        writeString(36, 'data');
        view.setUint32(40, dataLength, true);

        // Write PCM samples
        const offset = 44;
        for (let i = 0; i < samples.length; i++) {
            view.setInt16(offset + i * 2, samples[i], true);
        }

        return new Blob([buffer], { type: 'audio/wav' });
    };

    const handleDownloadMedia = async () => {
        const urlToDownload = submission.mediaUrls[currentMediaIndex] || null;
        if (!urlToDownload) return;

        setIsDownloading(true);

        try {
            // Use unified fetching with proxy fallback
            let blob = await fetchBlobWithProxy(urlToDownload);

            // Determine file extension from blob MIME type or fallback to media type
            let fileExtension = 'file';
            let shouldConvertAudio = false;

            // First, try to get extension from blob MIME type
            if (blob.type) {
                const mimeToExtension: Record<string, string> = {
                    'image/jpeg': 'jpg',
                    'image/jpg': 'jpg',
                    'image/png': 'png',
                    'image/gif': 'gif',
                    'image/webp': 'webp',
                    'video/mp4': 'mp4',
                    'video/webm': 'webm',
                    'video/quicktime': 'mov',
                    'audio/webm': 'webm',
                    'audio/mp3': 'mp3',
                    'audio/mpeg': 'mp3',
                    'audio/wav': 'wav',
                    'audio/ogg': 'ogg',
                };
                fileExtension = mimeToExtension[blob.type] || fileExtension;

                // Mark WebM audio for conversion to WAV
                if (blob.type === 'audio/webm') {
                    shouldConvertAudio = true;
                }
            }

            // Fallback to media type defaults if MIME type detection failed
            if (fileExtension === 'file') {
                const fallbackExtensions: Record<string, string> = {
                    [MediaType.Photo]: 'jpg',
                    [MediaType.Video]: 'mp4',
                    [MediaType.Screen]: 'webm',
                    [MediaType.Audio]: 'webm', // Changed from 'mp3' to 'webm' to match recording format
                };
                fileExtension = fallbackExtensions[mediaType] || fileExtension;

                // Mark audio for conversion
                if (mediaType === MediaType.Audio) {
                    shouldConvertAudio = true;
                }
            }

            // Convert WebM audio to WAV for better compatibility
            if (shouldConvertAudio) {
                blob = await convertWebMToWav(blob);
                fileExtension = 'wav';
            }

            // Last resort: try to extract from URL
            if (fileExtension === 'file') {
                const urlExtMatch = urlToDownload.split('.').pop();
                if (urlExtMatch) {
                    const ext = urlExtMatch.split('?')[0];
                    if (ext && ext.length <= 5) {
                        fileExtension = ext;
                    }
                }
            }

            const projectName = sanitizeFilename((currentProject && currentProject.name) || 'Project');
            const participantNameForFile = sanitizeFilename(participantName);
            const taskForFile = sanitizeFilename(taskName || task?.title || task?.prompt || 'task').slice(0, 40);
            const submissionId = submission.id;
            const formattedTimestamp = formatTimestampForFilename(submission.timestamp);

            const fileName = `${projectName}_${participantNameForFile}_${taskForFile}_${formattedTimestamp}_${submissionId}.${fileExtension}`;

            // Create download link from blob
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Clean up
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Download failed:", error);
            alert('Failed to download media. Please try again or check your connection.');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownloadAllAsZip = async () => {
        if (submission.mediaUrls.length === 0) return;

        setIsDownloading(true);

        try {
            const zip = new JSZip();
            const projectName = sanitizeFilename((currentProject && currentProject.name) || 'Project');
            const participantNameForFile = sanitizeFilename(participantName);
            const taskForFile = sanitizeFilename(taskName || task?.title || task?.prompt || 'task').slice(0, 40);
            const formattedTimestamp = formatTimestampForFilename(submission.timestamp);

            // Helper function to get extension from MIME type
            const getExtensionFromMimeType = (mimeType: string): string | null => {
                const mimeToExtension: Record<string, string> = {
                    'image/jpeg': 'jpg',
                    'image/jpg': 'jpg',
                    'image/png': 'png',
                    'image/gif': 'gif',
                    'image/webp': 'webp',
                    'video/mp4': 'mp4',
                    'video/webm': 'webm',
                    'video/quicktime': 'mov',
                    'audio/webm': 'webm',
                    'audio/mp3': 'mp3',
                    'audio/mpeg': 'mp3',
                    'audio/wav': 'wav',
                    'audio/ogg': 'ogg',
                };
                return mimeToExtension[mimeType] || null;
            };

            // Download all media files and add to zip
            for (let i = 0; i < submission.mediaUrls.length; i++) {
                const url = submission.mediaUrls[i];

                // Get blob with proxy fallback
                let blob = await fetchBlobWithProxy(url);

                // Determine file extension from blob MIME type
                let fileExtension = getExtensionFromMimeType(blob.type);

                // Fallback to media type defaults if MIME detection failed
                if (!fileExtension) {
                    const fallbackExtensions: Record<string, string> = {
                        [MediaType.Photo]: 'jpg',
                        [MediaType.Video]: 'mp4',
                        [MediaType.Screen]: 'webm',
                        [MediaType.Audio]: 'webm',
                    };
                    fileExtension = fallbackExtensions[mediaType] || 'file';
                }

                // Convert WebM audio to WAV for better compatibility
                if (blob.type === 'audio/webm' || (mediaType === MediaType.Audio && fileExtension === 'webm')) {
                    blob = await convertWebMToWav(blob);
                    fileExtension = 'wav';
                }

                // Add to zip with index
                const fileName = submission.mediaUrls.length > 1
                    ? `${i + 1}_${participantNameForFile}_${taskForFile}.${fileExtension}`
                    : `${participantNameForFile}_${taskForFile}.${fileExtension}`;

                zip.file(fileName, blob);
            }

            // Generate zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });

            // Download zip
            const url = URL.createObjectURL(zipBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${projectName}_${participantNameForFile}_${taskForFile}_${formattedTimestamp}_${submission.id}.zip`;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Download all failed:", error);
            alert('Failed to download all media. Please try again or check your connection.');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownloadText = () => {
        if (!currentNotes) return;
        const projectName = sanitizeFilename((currentProject && currentProject.name) || 'Project');
        const participantNameForFile = sanitizeFilename(participantName);
        const taskForFile = sanitizeFilename(taskName || task?.title || task?.prompt || 'task').slice(0, 40);
        const formattedTimestamp = formatTimestampForFilename(submission.timestamp);
        const fileName = `${projectName}_${participantNameForFile}_${taskForFile}_${formattedTimestamp}_text.txt`;

        const blob = new Blob([currentNotes], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handlePrevMedia = () => {
        let newIndex = (currentMediaIndex - 1 + submission.mediaUrls.length) % submission.mediaUrls.length;
        // If we have a dual stream (Screen + Face), face recordings are at odd indices (1, 3, 5...)
        // and should be skipped if we are navigating between main recordings.
        if (submission.mediaType === MediaType.Screen && submission.mediaUrls.length >= 2) {
            if (newIndex % 2 !== 0) {
                newIndex = (newIndex - 1 + submission.mediaUrls.length) % submission.mediaUrls.length;
            }
        }
        setCurrentMediaIndex(newIndex);
    };
    const handleNextMedia = () => {
        let newIndex = (currentMediaIndex + 1) % submission.mediaUrls.length;
        // If we have a dual stream (Screen + Face), skip face recordings (at odd indices)
        if (submission.mediaType === MediaType.Screen && submission.mediaUrls.length >= 2) {
            if (newIndex % 2 !== 0) {
                newIndex = (newIndex + 1) % submission.mediaUrls.length;
            }
        }
        setCurrentMediaIndex(newIndex);
    };

    const handleAddTag = () => {
        const trimmedTag = newTag.trim();
        if (trimmedTag && !currentTags.includes(trimmedTag)) {
            const updatedTags = [...currentTags, trimmedTag];
            setCurrentTags(updatedTags);
            onUpdateTags(submission.id, updatedTags);
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
        setCurrentTags(updatedTags);
        onUpdateTags(submission.id, updatedTags);
    };

    const handleAnalyzeAudio = async () => {
        const urlToAnalyze = submission.mediaUrls[currentMediaIndex];
        if (!urlToAnalyze) return;
        setIsAnalyzingAudio(true);
        console.log("[handleAnalyzeAudio] Starting analysis for:", urlToAnalyze);
        try {
            // Use unified fetching with proxy fallback
            const blob = await fetchBlobWithProxy(urlToAnalyze);

            const reader = new FileReader();

            reader.onerror = () => {
                console.error("[handleAnalyzeAudio] FileReader error:", reader.error);
                alert("Failed to read the media file for analysis.");
                setIsAnalyzingAudio(false);
            };

            reader.readAsDataURL(blob);
            reader.onloadend = async () => {
                console.log("[handleAnalyzeAudio] FileReader loaded Blob. Proceeding to Gemini analysis...");
                try {
                    const base64data = (reader.result as string).split(',')[1];
                    const mimeType = blob.type || 'audio/webm'; // Default fallback

                    // 3. Call Gemini
                    console.log(`[handleAnalyzeAudio] Invoking Gemini API with MIME type: ${mimeType}`);
                    const result = await generateAudioAnalysis(base64data, mimeType);
                    console.log("[handleAnalyzeAudio] Gemini API returned result:", result);

                    // 4. Update UI & DB
                    if (result.transcription) {
                        // Store transcript separately
                        setCurrentTranscript(result.transcription);
                        onUpdateTranscript?.(submission.id, result.transcription);
                    }

                    if (result.tags && result.tags.length > 0) {
                        const newTags = [...new Set([...currentTags, ...result.tags])];
                        setCurrentTags(newTags);
                        onUpdateTags(submission.id, newTags);
                    }
                    console.log("[handleAnalyzeAudio] Analysis complete.");
                } catch (geminiError) {
                    console.error("[handleAnalyzeAudio] Gemini API Error:", geminiError);
                    alert("Analysis failed. Please check your API Key and try again.");
                } finally {
                    setIsAnalyzingAudio(false);
                }
            };
        } catch (error) {
            console.error("[handleAnalyzeAudio] Audio Analysis Failed top catch:", error);
            alert("Failed to access audio file. Please ensure CORS is configured or URL is accessible.");
            setIsAnalyzingAudio(false);
        }
    };

    const renderMedia = () => {
        const showNavigation = submission.mediaUrls.length > 1;

        const NavigationControls = () => {
            if (!showNavigation) return null;
            return (
                <>
                    <button onClick={(e) => { e.stopPropagation(); handlePrevMedia(); }} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors z-20 active:scale-90" title="Previous Media">
                        <ChevronLeftIcon className="w-6 h-6" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleNextMedia(); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors z-20 active:scale-90" title="Next Media">
                        <ChevronRightIcon className="w-6 h-6" />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded-full z-20 pointer-events-none">
                        {mediaType === MediaType.Screen && submission.mediaUrls.length >= 2 ? (Math.floor(currentMediaIndex / 2) + 1) : (currentMediaIndex + 1)} / {mediaType === MediaType.Screen && submission.mediaUrls.length >= 2 ? Math.ceil(submission.mediaUrls.length / 2) : submission.mediaUrls.length}
                    </div>
                </>
            );
        };

        switch (mediaType) {
            case MediaType.Photo:
                if (submission.mediaUrls.length === 0) {
                    return <div className="text-center text-text-muted">No photos to display.</div>;
                }
                const hasPins = (submission.photoAnnotations?.[currentMediaIndex]?.pins?.length ?? 0) > 0;
                return (
                    <div className="flex flex-col w-full h-full bg-neutral-100 relative">
                        <div className="flex-grow submission-modal-split relative overflow-hidden">
                            <div className={`${hasPins ? 'submission-modal-photo' : 'w-full'} flex items-center justify-center bg-black/5 md:bg-app-bg md:p-8 relative overflow-hidden`}>
                                {/*
                            Mobile: w-full h-full flex items-center justify-center (container constraints height), image is object-contain.
                            Desktop: aspect-square w-full max-w-md, image is object-cover for neatness.
                        */}
                                <div
                                    className="relative group cursor-zoom-in flex items-center justify-center w-full h-full md:block md:w-full md:max-w-md md:h-auto md:aspect-square md:shadow-lg md:rounded-2xl md:overflow-hidden md:bg-white"
                                    onClick={() => setIsLightboxOpen(true)}
                                >
                                    {submission.photoAnnotations?.[currentMediaIndex] ? (
                                        <AnnotatedPhotoView
                                            imageUrl={submission.mediaUrls[currentMediaIndex]}
                                            annotation={submission.photoAnnotations[currentMediaIndex]}
                                            className="w-full h-full"
                                            objectFit="contain"
                                            activePinId={selectedPinId}
                                            onPinClick={setSelectedPinId}
                                        />
                                    ) : (
                                        <ImageWithLoader
                                            src={submission.mediaUrls[currentMediaIndex]}
                                            alt={`Submission by ${participantName}`}
                                            className="w-full h-full object-contain md:object-cover transition-transform duration-500 md:hover:scale-105"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center pointer-events-none">
                                        <div className="bg-white/90 p-3 rounded-full opacity-0 group-hover:opacity-100 transition-all transform scale-75 group-hover:scale-100 shadow-sm">
                                            <MaximizeIcon className="w-6 h-6 text-text-ink" />
                                        </div>
                                    </div>
                                </div>
                                <NavigationControls />
                            </div>
                            {hasPins && submission.photoAnnotations?.[currentMediaIndex] && (
                                <AnnotationTranscriptRail
                                    annotation={submission.photoAnnotations[currentMediaIndex]}
                                    selectedPinId={selectedPinId}
                                    onSelectPin={setSelectedPinId}
                                />
                            )}
                        </div>
                        {/* Thumbnails */}
                        {submission.mediaUrls.length > 1 && (
                            <div className="flex-shrink-0 p-3 bg-white border-t border-app-border overflow-x-auto">
                                <div className="flex justify-center items-center gap-2">
                                    {submission.mediaUrls.map((url, index) => {
                                        // Skip face recordings (odd indices) for dual-stream screen recordings
                                        if (submission.mediaType === MediaType.Screen && submission.mediaUrls.length >= 2 && index % 2 !== 0) {
                                            return null;
                                        }
                                        return (
                                            <button
                                                key={url + index}
                                                onClick={() => setCurrentMediaIndex(index)}
                                                className={`relative w-12 h-12 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all duration-200 active:scale-95 ${index === currentMediaIndex ? 'border-neutral-400 ring-2 ring-neutral-400/50' : 'border-transparent opacity-70 hover:opacity-100 grayscale hover:grayscale-0'}`}
                                            >
                                                <ImageWithLoader src={url} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            case MediaType.Video:
            case MediaType.Screen:
                // For Screen mediaType, we assume even indices (0, 2, 4...) are screen recordings
                // and odd indices (1, 3, 5...) are the corresponding face camera recordings for PiP.
                const isDualStream = mediaType === MediaType.Screen &&
                    currentMediaIndex % 2 === 0 &&
                    submission.mediaUrls.length > currentMediaIndex + 1;
                return (
                    <div ref={videoContainerRef} className="w-full h-full bg-black relative group flex items-center justify-center">
                        <video ref={videoRef} key={submission.mediaUrls[currentMediaIndex]} src={submission.mediaUrls[currentMediaIndex]} controls className="w-full h-full object-contain">Your browser does not support the video tag.</video>

                        {/* Custom Fullscreen Toggle for PiP support */}
                        <button
                            onClick={toggleFullscreen}
                            className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity z-30"
                            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                            {isFullscreen ? <MinimizeIcon className="w-5 h-5" /> : <MaximizeIcon className="w-5 h-5" />}
                        </button>

                        {isDualStream && showPip && (
                            <div className={`absolute left-6 w-32 md:w-48 aspect-video bg-black rounded-xl overflow-hidden border border-white/20 shadow-2xl z-20 transition-all duration-300 ring-1 ring-white/10 group-hover:scale-105 ${isFullscreen ? 'bottom-20' : 'top-6'}`}>
                                <video
                                    ref={faceVideoRef}
                                    src={submission.mediaUrls[currentMediaIndex + 1]}
                                    muted
                                    playsInline
                                    className="w-full h-full object-cover mirror"
                                    onLoadedMetadata={() => {
                                        if (videoRef.current && faceVideoRef.current) {
                                            faceVideoRef.current.currentTime = videoRef.current.currentTime;
                                        }
                                    }}
                                />
                                <button
                                    onClick={() => setShowPip(false)}
                                    className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                                >
                                    <XIcon className="w-3 h-3" />
                                </button>
                            </div>
                        )}
                        {!showPip && isDualStream && (
                            <button
                                onClick={() => setShowPip(true)}
                                className="absolute top-6 left-6 p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-md border border-white/10 z-20 text-[10px] font-bold uppercase tracking-wider transition-all"
                            >
                                Show Face Cam
                            </button>
                        )}
                        <NavigationControls />
                    </div>
                );
            case MediaType.Audio:
                return (
                    <div className="w-full h-full bg-app-bg flex flex-col items-center justify-center gap-6 p-6">
                        <div className="w-32 h-32 bg-white rounded-full shadow-float flex items-center justify-center text-status-warn border-4 border-white animate-pulse">
                            <MicIcon className="w-14 h-14" />
                        </div>
                        <div className="text-center">
                            <p className="font-bold text-text-ink text-lg">Audio Recording</p>
                            <div className="flex items-center justify-center gap-3 mt-2">
                                <p className="text-sm text-text-muted" style={{ textWrap: 'pretty' }}>Listen to the participant's response</p>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleAnalyzeAudio(); }}
                                    disabled={isAnalyzingAudio}
                                    className="px-3 py-1.5 bg-gradient-to-r from-action to-action-dark text-white rounded-full text-xs font-bold shadow-md hover:shadow-lg transform active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {isAnalyzingAudio ? (
                                        <>
                                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            Analyzing..
                                        </>
                                    ) : (
                                        'Analyze Audio'
                                    )}
                                </button>
                            </div>
                        </div>
                        {isWebmUrl(submission.mediaUrls[currentMediaIndex] || '') && (!WEBM_OPUS_PLAYABLE || audioBroken) ? (
                            <p className="text-sm text-text-muted italic mt-4 max-w-sm text-center">{WEBM_AUDIO_FALLBACK_TEXT}</p>
                        ) : (
                            <audio ref={audioRef} key={submission.mediaUrls[currentMediaIndex]} src={submission.mediaUrls[currentMediaIndex]} controls className="w-full max-w-sm shadow-md rounded-full bg-white mt-4" onError={() => setAudioBroken(true)}>Your browser does not support the audio element.</audio>
                        )}
                        <NavigationControls />
                    </div>
                );
            case MediaType.Text:
                return (
                    <div className="w-full h-full bg-app-bg overflow-y-auto">
                        <div className="min-h-full w-full flex flex-col items-center p-8 md:p-12">
                            <div className="my-auto w-full max-w-lg flex flex-col items-center gap-6">
                                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center flex-shrink-0 mx-auto">
                                    <TypeIcon className="w-8 h-8 text-action" />
                                </div>
                                {currentNotes && (
                                    <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-app-border w-full flex-shrink-0 text-left relative group">
                                        <p className="text-lg md:text-xl text-text-ink whitespace-pre-wrap leading-relaxed font-medium">"{currentNotes}"</p>
                                        <div className="mt-6 flex justify-center">
                                            <button
                                                onClick={handleDownloadText}
                                                className="flex items-center gap-2 px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-text-ink rounded-full text-xs font-bold uppercase tracking-wider transition-colors active:scale-95"
                                            >
                                                <DownloadIcon className="w-4 h-4" />
                                                Download Text
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {/* Display attached documents */}
                                {submission.mediaUrls.length > 0 && (
                                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-app-border w-full flex-shrink-0">
                                        <h4 className="text-xs font-bold text-text-faint uppercase tracking-wider mb-4 text-center">Attached Documents</h4>
                                        <div className="space-y-3">
                                            {submission.mediaUrls.map((url, index) => {
                                                // Extract filename from URL
                                                const urlParts = url.split('/');
                                                const encodedFilename = urlParts[urlParts.length - 1].split('?')[0];
                                                const filename = decodeURIComponent(encodedFilename);

                                                // Determine file type from URL or filename
                                                const docSvg = <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-text-muted"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>;
                                                let fileIcon: React.ReactNode = docSvg;
                                                let fileType = 'Document';
                                                if (filename.includes('.pdf') || url.includes('application%2Fpdf')) {
                                                    fileType = 'PDF';
                                                } else if (filename.includes('.txt') || url.includes('text%2Fplain')) {
                                                    fileType = 'Text';
                                                } else if (filename.includes('.doc') || url.includes('word')) {
                                                    fileType = 'Word';
                                                }

                                                return (
                                                    <div key={index} className="flex items-center justify-between p-4 bg-app-bg rounded-xl hover:bg-app-bg transition-colors group">
                                                        <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                                                            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-app-border">{fileIcon}</div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-bold text-text-ink truncate">{filename.split('_').slice(2).join('_') || `Document ${index + 1}`}</p>
                                                                <p className="text-xs text-text-muted">{fileType}</p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={async () => {
                                                                setIsDownloading(true);
                                                                try {
                                                                    const storagePathMatch = url.match(/\/o\/(.+?)\?/);
                                                                    if (!storagePathMatch) throw new Error('Invalid storage URL');

                                                                    const storagePath = decodeURIComponent(storagePathMatch[1]);
                                                                    const storageRef = ref(storage, storagePath);
                                                                    const blob = await getBlob(storageRef);

                                                                    const downloadUrl = URL.createObjectURL(blob);
                                                                    const a = document.createElement('a');
                                                                    a.href = downloadUrl;
                                                                    a.download = filename.split('_').slice(2).join('_') || `document_${index + 1}`;
                                                                    document.body.appendChild(a);
                                                                    a.click();
                                                                    document.body.removeChild(a);
                                                                    URL.revokeObjectURL(downloadUrl);
                                                                } catch (error) {
                                                                    console.error('Download failed:', error);
                                                                    alert('Failed to download document. Please try again.');
                                                                } finally {
                                                                    setIsDownloading(false);
                                                                }
                                                            }}
                                                            className="flex-shrink-0 p-2 bg-action text-white rounded-lg hover:bg-action-dark transition-colors active:scale-95"
                                                            disabled={isDownloading}
                                                        >
                                                            <DownloadIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            default:
                return <p>Unsupported media type</p>;
        }
    };

    const renderTags = () => {
        switch (tagState) {
            case 'loading':
                return <div className="text-sm text-text-muted animate-pulse flex items-center gap-2"><div className="w-4 h-4 border-2 border-status-warn border-t-transparent rounded-full animate-spin"></div> Generating smart tags...</div>;
            case 'error':
                return <div className="text-sm text-status-warn">Could not generate tags.</div>;
            case 'loaded':
            case 'idle':
            default:
                return (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap gap-2">
                            {currentTags.length === 0 && <div className="text-sm text-text-muted italic">No tags for this submission.</div>}
                            {currentTags.map(tag => (
                                <span key={tag} className="bg-neutral-100 text-text-muted text-xs font-semibold pl-3 pr-1 py-1 rounded-full flex items-center gap-0 transition-colors hover:bg-neutral-200 border border-app-border">
                                    {tag}
                                    <span className="w-px h-4 bg-neutral-300 mx-2"></span>
                                    <button onClick={() => handleRemoveTag(tag)} className="text-text-faint hover:text-text-ink rounded-full p-0.5 transition-colors">
                                        <XIcon className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                                placeholder="Add a tag..."
                                className="flex-grow bg-white border border-app-border rounded-lg text-sm px-3 py-2 focus:outline-none focus:border-action focus:ring-1 focus:ring-action transition-all"
                            />
                            <button onClick={handleAddTag} className="text-sm font-bold bg-neutral-100 text-text-muted px-4 py-2 rounded-lg hover:bg-neutral-200 border border-app-border transition-colors">Add</button>
                        </div>
                    </div>
                );
        }
    };

    const handleRequestRedo = async () => {
        if (!authUser || !claimsOrgId || !redoReason.trim()) return;
        setIsRequestingRedo(true);
        try {
            await requestRedo({
                submissionId: submission.id,
                organizationId: claimsOrgId,
                projectId: submission.projectId,
                participantId: submission.participantId,
                researcherUid: authUser.uid,
                researcherName: userProfile?.displayName || authUser.displayName || 'Researcher',
                reason: redoReason.trim(),
            });
            setRedoRequested(true);
            setShowRedoConfirm(false);
            setRedoReason('');
        } catch (err) {
            console.error('[SubmissionModal] requestRedo failed:', err);
        } finally {
            setIsRequestingRedo(false);
        }
    };

    const isMediaDownloadable = (mediaType === MediaType.Photo || mediaType === MediaType.Video || mediaType === MediaType.Audio || mediaType === MediaType.Screen) && submission.mediaUrls.length > 0;

    return createPortal(
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in-down" onClick={onClose}>
                <div
                    className="bg-white rounded-2xl shadow-2xl w-full md:max-w-5xl h-[90vh] md:h-[85vh] flex flex-col md:flex-row overflow-hidden relative"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Floating Action Buttons */}
                    <div className="absolute top-4 right-4 z-[50] flex items-center gap-2">
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowParticipantDetails(!showParticipantDetails); }}
                            className="p-2 bg-white/90 backdrop-blur-md rounded-full text-text-muted hover:text-action hover:bg-white border border-app-border transition-all duration-200"
                            aria-label="Toggle participant profile"
                            title="Participant Profile"
                        >
                            <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${showParticipantDetails ? 'rotate-180' : ''}`} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 bg-white/90 backdrop-blur-md rounded-full text-text-muted hover:text-status-warn hover:bg-white border border-app-border transition-all duration-200"
                            aria-label="Close modal"
                        >
                            <XIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Left Panel - Media (Split View) */}
                    {/* 
            Mobile: Fixed height (45vh) to allow details to be seen. Media takes up this space.
            Desktop: Full height. 
        */}
                    <div className="w-full md:w-1/2 bg-neutral-100 flex-shrink-0 relative border-b md:border-b-0 md:border-r border-app-border flex flex-col h-[45vh] md:h-full">
                        {renderMedia()}
                    </div>

                    {/* Right Panel - Details (Split View) */}
                    <div className="w-full md:w-1/2 flex flex-col bg-white h-full overflow-hidden relative min-h-0">
                        {/* Header */}
                        <div className="flex-shrink-0 pr-24 bg-white z-10">
                            <button
                                onClick={() => setShowParticipantDetails(!showParticipantDetails)}
                                className="w-full flex items-center p-6 pb-4 border-b border-app-border hover:bg-app-bg/50 transition-colors text-left"
                            >
                                {participant ? (
                                    <Avatar name={participant.name} avatarUrl={participant.avatarUrl} id={participant.id} className="w-12 h-12 rounded-full mr-4 object-cover ring-4 ring-neutral-50 shadow-sm" />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-neutral-200 flex items-center justify-center mr-4 flex-shrink-0">
                                        <UserIcon className="w-6 h-6 text-text-muted" />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-text-ink leading-tight">{participantName}</h3>
                                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                        <span className="px-2 py-0.5 bg-neutral-100 text-text-muted rounded text-xs font-bold uppercase tracking-wider">Participant</span>
                                        <span className="text-text-faint">|</span>
                                        <div className="flex items-center gap-1 text-xs text-text-muted font-medium">
                                            <ClockIcon className="w-3 h-3" />
                                            <span>{new Date(timestamp).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </button>

                            {/* Participant Details Expandable Section */}
                            {showParticipantDetails && participant && (
                                <div className="border-b border-app-border bg-app-bg/50 p-6 animate-fade-in-down">
                                    <h4 className="text-xs font-bold text-text-faint uppercase tracking-wider mb-4">Participant Profile</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        {participant.age && (
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                                    <CakeIcon className="w-4 h-4 text-action" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-text-muted mb-0.5">Age</p>
                                                    <p className="text-sm font-bold text-text-ink">{participant.age}</p>
                                                </div>
                                            </div>
                                        )}
                                        {participant.gender && (
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                                    <UsersIcon className="w-4 h-4 text-action" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-text-muted mb-0.5">Gender</p>
                                                    <p className="text-sm font-bold text-text-ink">{participant.gender}</p>
                                                </div>
                                            </div>
                                        )}
                                        {participant.location && (
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                                    <MapPinIcon className="w-4 h-4 text-chip-photo" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-text-muted mb-0.5">Location</p>
                                                    <p className="text-sm font-bold text-text-ink truncate">{participant.location}</p>
                                                </div>
                                            </div>
                                        )}
                                        {participant.occupation && (
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                                    <BriefcaseIcon className="w-4 h-4 text-status-ok" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-text-muted mb-0.5">Occupation</p>
                                                    <p className="text-sm font-bold text-text-ink truncate">{participant.occupation}</p>
                                                </div>
                                            </div>
                                        )}
                                        {participant.email && (
                                            <div className="flex items-start gap-3 col-span-2">
                                                <div className="flex-shrink-0 w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                                    <MailIcon className="w-4 h-4 text-text-muted" />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-text-muted mb-0.5">Email</p>
                                                    <p className="text-sm font-bold text-text-ink truncate">{participant.email}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-grow overflow-y-auto p-6 space-y-8 bg-white custom-scrollbar">
                            {/* Task Prompt Section */}
                            {taskName && (
                                <div className="animate-fade-in-down" style={{ animationDelay: '0.05s' }}>
                                    <h4 className="text-xs font-bold text-text-faint uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <span>Task Prompt</span>
                                        <div className="h-px bg-neutral-100 flex-grow"></div>
                                    </h4>
                                    <div className="flex items-start gap-3 bg-app-bg p-4 rounded-xl border border-app-border">
                                        <div className="p-2 bg-white rounded-lg text-text-faint flex-shrink-0">
                                            <CheckSquareIcon className="w-5 h-5" />
                                        </div>
                                        <p className="text-text-ink font-medium text-sm pt-1" style={{ textWrap: 'pretty' }}>{taskName}</p>
                                    </div>
                                </div>
                            )}

                            {/* Notes Section */}
                            {mediaType !== MediaType.Text && (
                                <div className="animate-fade-in-down" style={{ animationDelay: '0.1s' }}>
                                    <h4 className="text-xs font-bold text-text-faint uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <span>Notes</span>
                                        <div className="h-px bg-neutral-100 flex-grow"></div>
                                        {currentNotes && (
                                            <button
                                                onClick={() => {
                                                    safeClipboardWrite(currentNotes);
                                                    alert('Notes copied to clipboard!');
                                                }}
                                                className="flex items-center gap-1.5 px-2 py-1 bg-neutral-100 hover:bg-neutral-200 text-text-muted rounded text-xs font-medium transition-colors active:scale-95"
                                                title="Copy to clipboard"
                                            >
                                                <ClipboardCopyIcon className="w-3.5 h-3.5" />
                                                Copy
                                            </button>
                                        )}
                                    </h4>
                                    <div className="text-text-ink whitespace-pre-wrap leading-relaxed text-sm bg-app-bg p-5 rounded-xl border border-app-border" style={{ textWrap: 'pretty' }}>
                                        {currentNotes ? currentNotes : <span className="text-text-faint italic">No notes provided by participant.</span>}
                                    </div>
                                </div>
                            )}

                            {/* Transcript Section */}
                            {currentTranscript && (
                                <div className="animate-fade-in-down" style={{ animationDelay: '0.15s' }}>
                                    <h4 className="text-xs font-bold text-text-faint uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <span>Transcript</span>
                                        <div className="h-px bg-neutral-100 flex-grow"></div>
                                        <button
                                            onClick={() => {
                                                safeClipboardWrite(currentTranscript);
                                                alert('Transcript copied to clipboard!');
                                            }}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-neutral-100 hover:bg-neutral-200 text-text-muted rounded text-xs font-medium transition-colors active:scale-95"
                                            title="Copy transcript to clipboard"
                                        >
                                            <ClipboardCopyIcon className="w-3.5 h-3.5" />
                                            Copy
                                        </button>
                                    </h4>
                                    <div className="text-text-ink leading-relaxed text-sm bg-app-bg p-5 rounded-xl border border-app-border flex flex-col gap-4">
                                        {currentTranscript.split('\n\n').map((paragraph, index) => {
                                            if (!paragraph.trim()) return null;

                                            // Extract timestamp if present [HH:MM:SS] or [MM:SS]
                                            const timeMatch = paragraph.match(/\[(\d{2}:)?\d{2}:\d{2}\]/);
                                            const timeStr = timeMatch ? timeMatch[0].replace(/[\[\]]/g, '') : null;

                                            return (
                                                <div key={index} className="relative group p-2 -m-2 rounded-lg hover:bg-app-bg transition-colors">
                                                    <p className="whitespace-pre-wrap" style={{ textWrap: 'pretty' }}>{renderHighlightedText(paragraph, highlightQuote)}</p>

                                                    {/* Floating Action Bar */}
                                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-all flex items-center bg-neutral-900 border border-neutral-700 text-white shadow-lg overflow-hidden translate-y-[-50%] scale-95 origin-top-right group-hover:scale-100 pointer-events-none group-hover:pointer-events-auto z-10 rounded-md">
                                                        <button
                                                            className="flex items-center gap-1.5 px-3 py-1.5 hover:bg-neutral-800 text-xs font-medium border-r border-neutral-700 transition-colors"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const concept = prompt("Add a new concept/tag based on this segment:");
                                                                if (concept && concept.trim() !== '') {
                                                                    const newTagsList = Array.from(new Set([...currentTags, concept.trim()]));
                                                                    setCurrentTags(newTagsList);
                                                                    onUpdateTags(submission.id, newTagsList);
                                                                }
                                                            }}
                                                        >
                                                            <EditIcon className="w-3 h-3" />
                                                            Edit concepts
                                                        </button>
                                                        <button
                                                            className="p-1.5 px-2 hover:bg-neutral-800 border-r border-neutral-700 transition-colors"
                                                            title="Add to Notes"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const updatedNotes = currentNotes ? currentNotes + '\n\n' + paragraph : paragraph;
                                                                setCurrentNotes(updatedNotes);
                                                                onUpdateNotes(submission.id, updatedNotes);
                                                                alert("Segment added to Notes!");
                                                            }}
                                                        >
                                                            <GridIcon className="w-3.5 h-3.5" />
                                                        </button>
                                                        {timeStr && (
                                                            <button
                                                                className="p-1.5 px-2 hover:bg-neutral-800 transition-colors text-white"
                                                                title="Play from here"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const parts = timeStr.trim().split(':').map(Number);
                                                                    let seconds = 0;
                                                                    if (parts.length === 3) seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
                                                                    if (parts.length === 2) seconds = parts[0] * 60 + parts[1];

                                                                    if (submission.mediaType === MediaType.Video && videoRef.current) {
                                                                        videoRef.current.currentTime = seconds;
                                                                        videoRef.current.play().catch(e => console.error("Auto-play prevented", e));
                                                                    } else if (submission.mediaType === MediaType.Audio && audioRef.current) {
                                                                        audioRef.current.currentTime = seconds;
                                                                        audioRef.current.play().catch(e => console.error("Auto-play prevented", e));
                                                                    }
                                                                }}
                                                            >
                                                                <PlayIcon className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Tags Section */}
                            <div className="animate-fade-in-down" style={{ animationDelay: '0.2s' }}>
                                <h4 className="text-xs font-bold text-text-faint uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span>Analysis Tags</span>
                                    <div className="h-px bg-neutral-100 flex-grow"></div>
                                </h4>
                                {renderTags()}
                            </div>

                            {/* Conversation Thread */}
                            {authUser && submission.participantId && (
                            <div className="animate-fade-in-down" style={{ animationDelay: '0.25s' }}>
                                <h4 className="text-xs font-bold text-text-faint uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span>Conversation</span>
                                    <div className="h-px bg-neutral-100 flex-grow"></div>
                                </h4>
                                <ConversationThread
                                    submissionId={submission.id}
                                    organizationId={claimsOrgId || submission.organizationId || ''}
                                    projectId={submission.projectId}
                                    participantId={submission.participantId}
                                    currentUserUid={authUser.uid}
                                    currentUserName={userProfile?.displayName || authUser.displayName || 'Researcher'}
                                    currentUserRole="researcher"
                                    taskName={taskName || task?.title || task?.prompt}
                                />
                            </div>
                            )}

                            {/* Request Redo */}
                            {canRequestRedo && authUser && (
                            <div className="animate-fade-in-down" style={{ animationDelay: '0.28s' }}>
                                {!showRedoConfirm ? (
                                    <button
                                        onClick={() => setShowRedoConfirm(true)}
                                        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
                                    >
                                        <RefreshCwIcon className="w-4 h-4" />
                                        Request Redo
                                        {task?.maxAttempts && (
                                            <span className="text-xs font-normal opacity-70">
                                                ({attemptCount}/{task.maxAttempts} attempts used)
                                            </span>
                                        )}
                                    </button>
                                ) : (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-3">
                                        <p className="text-sm font-semibold text-amber-800">Why should this be redone?</p>
                                        <textarea
                                            value={redoReason}
                                            onChange={(e) => setRedoReason(e.target.value)}
                                            placeholder="Explain what needs to change…"
                                            className="w-full text-sm bg-white border border-amber-200 rounded-lg p-3 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 resize-none"
                                            rows={3}
                                            autoFocus
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => { setShowRedoConfirm(false); setRedoReason(''); }}
                                                className="flex-1 py-2 rounded-lg text-sm font-semibold text-text-muted bg-neutral-100 hover:bg-neutral-200 transition-colors"
                                                disabled={isRequestingRedo}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleRequestRedo}
                                                disabled={!redoReason.trim() || isRequestingRedo}
                                                className="flex-1 py-2 rounded-lg text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 transition-colors disabled:opacity-50"
                                            >
                                                {isRequestingRedo ? 'Sending…' : 'Send Redo Request'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            )}

                            {/* Redo already requested badge */}
                            {redoRequested && (
                            <div className="animate-fade-in-down flex items-center gap-2 py-2 px-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
                                <RefreshCwIcon className="w-3.5 h-3.5" />
                                Redo requested — waiting for participant
                            </div>
                            )}

                            {/* Metadata Section */}
                            <div className="animate-fade-in-down" style={{ animationDelay: '0.3s' }}>
                                <h4 className="text-xs font-bold text-text-faint uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span>Metadata</span>
                                    <div className="h-px bg-neutral-100 flex-grow"></div>
                                </h4>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex items-center gap-3 text-text-muted bg-app-bg p-3 rounded-xl border border-app-border">
                                        <div className="p-2 bg-white rounded-lg text-text-faint">
                                            <MapPinIcon className="w-4 h-4" />
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="text-xs text-text-faint font-bold mb-0.5">Location</p>
                                            <p className="truncate font-medium text-text-ink" title={location} style={{ textWrap: 'pretty' }}>{location || 'Unknown'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 text-text-muted bg-app-bg p-3 rounded-xl border border-app-border">
                                        <div className="p-2 bg-white rounded-lg text-text-faint">
                                            <ClockIcon className="w-4 h-4" />
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="text-xs text-text-faint font-bold mb-0.5">Time</p>
                                            <p className="truncate font-medium text-text-ink">{new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="p-5 pt-4 border-t border-app-border flex items-center justify-between gap-4 flex-wrap flex-shrink-0 z-10 w-full bg-white">
                            <div className="flex flex-wrap items-center gap-2">
                                {/* Download All as ZIP button (shown when multiple media) */}
                                {isMediaDownloadable && submission.mediaUrls.length > 1 && (
                                    <button
                                        onClick={handleDownloadAllAsZip}
                                        disabled={isDownloading}
                                        className="flex items-center gap-2 bg-neutral-100 text-text-muted font-semibold py-2.5 px-4 rounded-full hover:bg-neutral-200 border border-app-border transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <DownloadIcon className="w-4 h-4" />
                                        <span>{isDownloading ? '...' : `Download All (${submission.mediaUrls.length})`}</span>
                                    </button>
                                )}

                                {isMediaDownloadable && (
                                    <button
                                        onClick={handleDownloadMedia}
                                        disabled={isDownloading}
                                        className="flex items-center gap-2 bg-neutral-100 text-text-muted font-semibold py-2.5 px-4 rounded-full hover:bg-neutral-200 border border-app-border transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <DownloadIcon className="w-4 h-4" />
                                        <span>{isDownloading ? '...' : submission.mediaUrls.length > 1 ? 'Current' : 'Download'}</span>
                                    </button>
                                )}
                                <button
                                    onClick={handleExportJson}
                                    className="flex items-center gap-2 bg-neutral-100 text-text-muted font-semibold py-2.5 px-4 rounded-full hover:bg-neutral-200 border border-app-border transition-colors text-sm"
                                >
                                    <DownloadIcon className="w-4 h-4" />
                                    <span>Export</span>
                                </button>
                                <button
                                    onClick={onShare}
                                    className="flex items-center gap-2 bg-neutral-100 text-text-muted font-semibold py-2.5 px-4 rounded-full hover:bg-neutral-200 border border-app-border transition-colors text-sm"
                                >
                                    <ShareIcon className="w-4 h-4" />
                                    <span>Share</span>
                                </button>
                            </div>

                            {/* Mark as Reviewed Toggle */}
                            {onToggleReview && (
                                <button
                                    onClick={() => onToggleReview(submission.id)}
                                    className={`flex items-center gap-2 font-bold py-2.5 px-6 rounded-full transition-all text-sm shrink-0 whitespace-nowrap
                                        ${submission.isReviewed
                                            ? 'bg-status-ok-bg text-status-ok hover:bg-status-ok-bg border border-status-ok'
                                            : 'bg-white text-action hover:bg-app-bg shadow-sm border border-app-border hover:border-action/30 ring-1 ring-black/5'
                                        }`}
                                >
                                    <CheckIcon className={`w-4 h-4 ${submission.isReviewed ? 'text-status-ok' : 'text-action/40'}`} />
                                    {submission.isReviewed ? 'Reviewed' : 'Mark as Reviewed'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isLightboxOpen && (
                <Lightbox
                    mediaType={mediaType}
                    mediaUrl={submission.mediaUrls[currentMediaIndex]}
                    onClose={() => setIsLightboxOpen(false)}
                />
            )}
        </>,
        document.body
    );
};

export default SubmissionModal;
